import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { invalidateMany } from './queryUtils';
import {
  getRobotTasks,
  cancelRobotTask,
  retryRobotTask,
  type RobotTaskRow,
  type RobotTaskFilters,
  type RobotTaskActionResult,
} from '../services/api/robotTasks';

/**
 * Phase 115 (Plan 115-06 / PCOPS-02) — TanStack Query hooks for
 * `/admin/robot-tasks`. Follows `useBotAdmin.ts`'s query-key and
 * invalidation conventions: mutations invalidate the list on success only
 * (server-confirm, no optimistic update — a wrong optimistic cancel/retry
 * state would be actively misleading for an operator).
 */

function robotTasksQueryKey(filters: RobotTaskFilters) {
  return ['robot-tasks', filters] as const;
}

/**
 * A cancel or retry is confirmed by the server BEFORE its effect exists: the
 * cancel reaches Factory™ through a queue job and comes back as a pushed
 * `cancelled`, and a retry's new issue is correlated only after its dispatch
 * job runs. The immediate invalidation therefore re-reads the OLD state
 * (found in the Phase 115 staging walk — the row kept saying `dispatched`
 * until a reload). Re-read again once the round trip has had time to land.
 */
export const ROBOT_TASK_FOLLOW_UP_REFETCH_MS = [2_000, 6_000] as const;

function invalidateRobotTasksNowAndAfterRoundTrip(queryClient: QueryClient) {
  invalidateMany(queryClient, [['robot-tasks']]);
  for (const delay of ROBOT_TASK_FOLLOW_UP_REFETCH_MS) {
    setTimeout(() => invalidateMany(queryClient, [['robot-tasks']]), delay);
  }
}

export function useRobotTasks(filters: RobotTaskFilters = {}) {
  return useQuery<RobotTaskRow[]>({
    queryKey: robotTasksQueryKey(filters),
    queryFn: () => getRobotTasks(filters),
    staleTime: 15 * 1000,
  });
}

export function useCancelRobotTask() {
  const queryClient = useQueryClient();
  return useMutation<RobotTaskActionResult, Error, number>({
    mutationFn: (taskId: number) => cancelRobotTask(taskId),
    onSuccess: () => {
      invalidateRobotTasksNowAndAfterRoundTrip(queryClient);
    },
  });
}

export function useRetryRobotTask() {
  const queryClient = useQueryClient();
  return useMutation<RobotTaskActionResult, Error, number>({
    mutationFn: (taskId: number) => retryRobotTask(taskId),
    onSuccess: () => {
      invalidateRobotTasksNowAndAfterRoundTrip(queryClient);
    },
  });
}
